import { openDB } from "idb";
import type { LocalTransactionStore, Platform } from "../ports";

const STORES = [
  "orders",
  "outbox",
  "catalogue",
  "offline-leases",
  "offline-snapshots",
  "offline-meta",
] as const;

export interface WebPlatformOptions {
  /** Separate database names keep browser conformance runs isolated. */
  databaseName?: string;
  /**
   * A newer build opened the database to upgrade it (R1-42). This connection closes, so the upgrade
   * isn't blocked: transactions already running finish first, and later commits fail rather than
   * report a sale this tab could no longer store. The app should reload at a safe checkpoint.
   */
  onVersionChange?: () => void;
}

export async function createWebPlatform({
  databaseName = "kopi-pos",
  onVersionChange,
}: WebPlatformOptions = {}): Promise<Platform> {
  let refuseBlocked: (error: Error) => void = () => {};
  const blocked = new Promise<never>((_, reject) => {
    refuseBlocked = reject;
  });
  const opening = openDB(databaseName, 3, {
    // An older tab that can't let go of the database (it predates `blocking`): fail the start-up with
    // a retryable error instead of hanging; closing that tab and retrying opens it.
    blocked() {
      refuseBlocked(new Error("local_storage_blocked"));
    },
    blocking() {
      db.close();
      onVersionChange?.();
    },
    upgrade(database, oldVersion) {
      for (const name of STORES) {
        if (oldVersion < 1 && !database.objectStoreNames.contains(name)) database.createObjectStore(name);
      }
      if (oldVersion < 2) {
        for (const name of ["offline-leases", "offline-snapshots"] as const) {
          if (!database.objectStoreNames.contains(name)) database.createObjectStore(name);
        }
      }
      if (oldVersion < 3 && !database.objectStoreNames.contains("offline-meta"))
        database.createObjectStore("offline-meta");
    },
  });
  const db = await Promise.race([opening, blocked]);

  const store: LocalTransactionStore = {
    async commit(records) {
      const names = [...new Set(records.map((r) => r.store))];
      const tx = db.transaction(names, "readwrite");
      try {
        await Promise.all([...records.map((r) => tx.objectStore(r.store).put(r.value, r.key)), tx.done]);
      } catch (error) {
        try {
          tx.abort();
        } catch {
          // The transaction may already have aborted because the request failed.
        }
        await tx.done.catch(() => undefined);
        throw error;
      }
    },
    get: (storeName, key) => db.get(storeName, key),
  };

  return { kind: "web", store };
}
